import { useSale } from "../../../contexts/SaleContext"
import { useEffect, useState } from "react"
import { TableColumnsType, Tag, Button, Tooltip } from "antd"
import TableView from "../TableView"
import { useConfig } from "../../../contexts/ConfigContext"
import { useCompany } from "../../../contexts/CompanyContext"
import useAuth from "../../../hooks/useAuth"
import { useSales } from "../../../hooks/useSale"
import { saleStatus } from "../../../config/const"
import { getDateFilter, textNumberFormater, formatDateTime } from "../../../utils"
import HeaderElements from "./HeaderElements";
import { useHistory } from "react-router-dom";
import {
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CheckOutlined
} from "@ant-design/icons";
import { mutate } from "swr";
import RegisterBillPaymentModal from "../../RegisterBillPaymentModal";

export const getSaleGeneralStatus = (record: any) => {
  const total = Number(record.total_amount_net || record.total_amount || 0);
  const totalPaid = Number(record.total_paid_amount || 0);
  const balanceDue = Number(
    record.balance_due_amount ?? Math.max(0, Math.round(((total - totalPaid) + Number.EPSILON) * 100) / 100)
  );

  const isDeferred = record.execution_mode === 'DEFERRED';

  // 1. Estado de Entrega Física
  let deliveryStatus: 'DELIVERED' | 'PARTIALLY_DELIVERED' | 'PENDING_DELIVERY' = 'DELIVERED';
  if (record.reception_status === 'RECEIVED') {
    deliveryStatus = 'DELIVERED';
  } else if (record.reception_status === 'PARTIALLY_RECEIVED') {
    deliveryStatus = 'PARTIALLY_DELIVERED';
  } else if (record.reception_status === 'PENDING') {
    deliveryStatus = 'PENDING_DELIVERY';
  } else if (isDeferred) {
    deliveryStatus = 'PENDING_DELIVERY';
  } else {
    deliveryStatus = 'DELIVERED';
  }

  // 2. Estado de Cartera / Pago
  const isCredit = record.payment_term === 'CREDIT';
  let paymentStatus: 'PAID' | 'PARTIALLY_PAID' | 'UNPAID' = 'PAID';
  if (balanceDue <= 0 && (totalPaid > 0 || total === 0 || record.payment_status === 'PAID')) {
    paymentStatus = 'PAID';
  } else if (totalPaid > 0 && balanceDue > 0) {
    paymentStatus = 'PARTIALLY_PAID';
  } else if (balanceDue > 0 || record.payment_status === 'UNPAID' || isCredit) {
    paymentStatus = 'UNPAID';
  }

  // 3. Liquidada al 100%
  const isSettled = record.settlement_status === 'SETTLED' ||
    (deliveryStatus === 'DELIVERED' && paymentStatus === 'PAID') ||
    (!isDeferred && balanceDue <= 0 && (record.status === 'COMPLETED' || record.status === 'APPROVED'));

  return {
    isSettled,
    deliveryStatus,
    paymentStatus,
    balanceDue,
    totalPaid,
    total,
    isDeferred,
    isCredit
  };
};


function ViewBillList(){
  const { sale, updateSale} = useSale()
  const {config} = useConfig()
  const {company} = useCompany()
  const history = useHistory()
  const [spinning, setSpinning] = useState(false)
  const [skeleton, setSkeleton] = useState(true)
  const [selectedBillForPayment, setSelectedBillForPayment] = useState<any>(null)
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const {tokenData} = useAuth.useToken('authCsrfToken',config.session,config.key)

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const {data:billData,isLoading} = useSales(tokenData,{
    company:company.current.uid,
    status:saleStatus.COMPLETED,
    documentType:"SALE_INVOICE",
    first: false,
    module: config.module,
    filter: getDateFilter(sale.datePickerFilter),
    page,
    hitsPerPage: pageSize
  },company.current.id)
  
  useEffect(()=>{
    if(tokenData && billData?.movementList){
      console.log("Bills",billData.movementList)
      updateSale({billList:billData.movementList})
      setSkeleton(false)
    }

  },[tokenData,billData])

  //console.log('Load content ',companies.current)
  let title = 'Ventas registradas'
  let emptyMessage = 'No hay ventas registradas, modifique su consulta'
  
  const handleClick = (data:any) => {
    history.push(`/bill-detail/${data.uid}`)
  }

  const columsBillList:TableColumnsType = [
    {title: "#",dataIndex:"register", width: 50},
    {title: "Registro",dataIndex:"document_number"},
    {
      title: "Tipo / DIAN",
      key: "fe_status",
      render: (_, record: any) => {
        const isFE = record.invoice_type === 'ELECTRONIC_INVOICE' || record.electronic_invoice_number || record.electronic_invoice_status;
        if (!isFE) return <Tag color="default">Comprobante</Tag>;
        
        if (record.electronic_invoice_status === 'ACCEPTED') {
          return <Tag color="success">FE Validada</Tag>;
        }
        if (record.electronic_invoice_status === 'PENDING') {
          return <Tag color="warning">FE Pendiente</Tag>;
        }
        if (record.electronic_invoice_status === 'REJECTED') {
          return <Tag color="error">FE Rechazada</Tag>;
        }
        return <Tag color="blue">Factura Elec.</Tag>;
      }
    },
    {title: "Fecha facturación",dataIndex:"document_date",render:(text: string, record: any)=>formatDateTime(text || record.created_at || record.updated_at)},
    {title: "Items",dataIndex:"items"},
    {title: "Cliente",dataIndex:"contact_name"},
    {title: "Vendedor",dataIndex:"created_by_user_name"},
    {title: "Total",dataIndex:"total_amount_net",render:(text)=><label className="numeric-values-data-table">{textNumberFormater(text)}</label>},
    {
      title: "Estado General",
      key: "general_status",
      render: (_, record: any) => {
        const { isSettled, deliveryStatus, paymentStatus, balanceDue } = getSaleGeneralStatus(record);

        if (isSettled) {
          return (
            <Tooltip title="Venta 100% liquidada: Mercancía entregada y pago completado en su totalidad">
              <Tag
                color="success"
                icon={<CheckCircleOutlined />}
                style={{
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 12,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                100% Liquidada
              </Tag>
            </Tooltip>
          );
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            {/* Estado de entrega física */}
            {deliveryStatus === 'DELIVERED' && (
              <Tooltip title="Mercancía entregada al cliente">
                <Tag
                  color="blue"
                  icon={<CheckOutlined />}
                  style={{ fontSize: '11px', margin: 0, padding: '1px 6px', borderRadius: 4 }}
                >
                  Mercancía: Entregada
                </Tag>
              </Tooltip>
            )}
            {deliveryStatus === 'PARTIALLY_DELIVERED' && (
              <Tooltip title="Entrega parcial de mercancía realizada">
                <Tag
                  color="cyan"
                  icon={<ClockCircleOutlined />}
                  style={{ fontSize: '11px', margin: 0, padding: '1px 6px', borderRadius: 4 }}
                >
                  Mercancía: Entrega Parcial
                </Tag>
              </Tooltip>
            )}
            {deliveryStatus === 'PENDING_DELIVERY' && (
              <Tooltip title="Mercancía pendiente por despachar / entregar al cliente">
                <Tag
                  color="volcano"
                  icon={<ClockCircleOutlined />}
                  style={{ fontSize: '11px', margin: 0, padding: '1px 6px', borderRadius: 4 }}
                >
                  Mercancía: Por Entregar
                </Tag>
              </Tooltip>
            )}

            {/* Estado de cartera / tesorería */}
            {paymentStatus === 'PAID' && (
              <Tooltip title="Factura pagada en su totalidad">
                <Tag
                  color="green"
                  icon={<CheckCircleOutlined />}
                  style={{ fontSize: '11px', margin: 0, padding: '1px 6px', borderRadius: 4 }}
                >
                  Pago: Pagada
                </Tag>
              </Tooltip>
            )}
            {paymentStatus === 'PARTIALLY_PAID' && (
              <Tooltip title={`Falta por pagar: Saldo pendiente de ${textNumberFormater(balanceDue)}`}>
                <Tag
                  color="warning"
                  icon={<DollarOutlined />}
                  style={{ fontSize: '11px', margin: 0, padding: '1px 6px', borderRadius: 4 }}
                >
                  Pago: Abonada (Falta pago)
                </Tag>
              </Tooltip>
            )}
            {paymentStatus === 'UNPAID' && (
              <Tooltip title={`Factura pendiente de pago. Saldo total: ${textNumberFormater(balanceDue)}`}>
                <Tag
                  color="error"
                  icon={<CloseCircleOutlined />}
                  style={{ fontSize: '11px', margin: 0, padding: '1px 6px', borderRadius: 4 }}
                >
                  Pago: Por Pagar
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: "Saldo Pendiente",
      key: "balance_due",
      align: "right",
      render: (_, record: any) => {
        const total = Number(record.total_amount_net || record.total_amount || 0);
        const totalPaid = Number(record.total_paid_amount || 0);
        const balanceDue = Number(
          record.balance_due_amount ?? Math.max(0, Math.round(((total - totalPaid) + Number.EPSILON) * 100) / 100)
        );

        if (balanceDue > 0) {
          return (
            <label className="numeric-values-data-table" style={{ color: "#f5222d", fontWeight: 600 }}>
              {textNumberFormater(balanceDue)}
            </label>
          );
        }
        return <label className="numeric-values-data-table" style={{ color: "#52c41a" }}>{"$0"}</label>;
      }
    },
    {
      title: "Acción",
      key: "action",
      align: "center",
      render: (_, record: any) => {
        const total = Number(record.total_amount_net || record.total_amount || 0);
        const totalPaid = Number(record.total_paid_amount || 0);
        const balanceDue = Number(
          record.balance_due_amount ?? Math.max(0, Math.round(((total - totalPaid) + Number.EPSILON) * 100) / 100)
        );

        if (balanceDue > 0) {
          return (
            <Button
              size="small"
              type="primary"
              icon={<DollarOutlined />}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedBillForPayment(record);
                setPaymentModalVisible(true);
              }}
            >
              Abonar
            </Button>
          );
        }
        return (
          <Button
            size="small"
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/bill-detail/${record.uid}`);
            }}
          >
            Ver
          </Button>
        );
      }
    }
  ]

  const tableColumns = columsBillList
  const dataTable = sale.billList.map((el:any,i:number)=>{
    el.register = i + 1
    el.key = i  

    return el
  })

  const handlePageChange = (newPage: number, newPageSize?: number) => {
    setPage(newPage - 1) // Convert to 0-based for Algolia
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize)
      setPage(0) // Reset to first page when page size changes
    }
  }

  const headerElement = () => <HeaderElements setSpinning={setSpinning}/>

  return (
    <>
      <TableView
        title={title}
        spinning={spinning || isLoading}
        skeleton={skeleton}
        tableColumns={tableColumns}
        dataTable={dataTable}
        onRow={(data:any,index:any) => {return{onClick:()=>handleClick(data),style:{cursor:"pointer"}}}}
        emptyMessage={emptyMessage}
        headerElement={headerElement}
        pagination={{
          current: page + 1,
          pageSize,
          total: billData?.nbHits || 0,
          onChange: handlePageChange,
          showSizeChanger: false,
          showTotal: (total:number) => `Total ${total} facturas`
        }}
      >
      </TableView>

      <RegisterBillPaymentModal
        visible={paymentModalVisible}
        onClose={() => {
          setPaymentModalVisible(false);
          setSelectedBillForPayment(null);
        }}
        movement={selectedBillForPayment}
        onSuccess={() => {
          mutate(
            (key) => Array.isArray(key) && typeof key[0] === 'string' && key[0].includes('/inventory/movement'),
            undefined,
            { revalidate: true }
          );
        }}
      />
    </>
  )
}

export default ViewBillList